<template>
  <div class="profile-container">
    <!-- Hero Section with Gradient Background -->
    <div class="hero-section">
      <div class="hero-gradient"></div>
      <div class="hero-pattern"></div>
    </div>

    <!-- Main Content -->
    <v-container fluid class="main-content">
      <v-row justify="center">
        <v-col cols="12" lg="10" xl="8">
          <!-- Profile Header Card -->
          <v-card 
            class="profile-header-card elevation-12" 
            :class="{ 'mobile-card': $vuetify.breakpoint.smAndDown }"
          >
            <div class="profile-header-content">
              <!-- Avatar Section -->
              <div class="avatar-section">
                <div class="avatar-container">
                  <editable-avatar
                    :image="profile.midThumbnail"
                    :size="$vuetify.breakpoint.smAndDown ? 80 : 140"
                    :editable="!isViewMode"
                    :onImageSelected="onImageSelected"
                    class="profile-avatar"
                  />
                  <div class="avatar-status-indicator"></div>
                </div>
                
                <!-- Profile Info -->
                <div class="profile-info">
                  <h1 class="profile-name">
                    {{ profile.first_name }} {{ profile.last_name }}
                  </h1>
                  <p class="profile-email">{{ profile.email }}</p>
                  <p class="profile-phone">{{ profile.phone }}</p>
                  <div class="profile-badges">
                    <v-chip 
                      small 
                      color="primary" 
                      text-color="white"
                      class="mr-2 mb-1"
                    >
                      <v-icon left small>mdi-shield-check</v-icon>
                      Verified
                    </v-chip>
                    <v-chip 
                      small 
                      color="success" 
                      text-color="white"
                      class="mb-1"
                    >
                      <v-icon left small>mdi-account-check</v-icon>
                      Active
                    </v-chip>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="action-buttons">
                <v-btn
                  :color="isViewMode ? 'primary' : 'grey'"
                  :outlined="!isViewMode"
                  @click="toggleViewMode"
                  class="action-btn"
                  block
                >
                  <v-icon left>{{ isViewMode ? 'mdi-pencil' : 'mdi-eye' }}</v-icon>
                  {{ isViewMode ? 'Edit Profile' : 'View Profile' }}
                </v-btn>
              </div>
            </div>
          </v-card>

          <!-- Main Content Tabs -->
          <v-card class="main-content-card elevation-8 mt-6">
            <v-tabs 
              v-model="tab" 
              background-color="transparent"
              color="primary"
              slider-color="primary"
              class="modern-tabs"
              :centered="$vuetify.breakpoint.smAndDown"
              :show-arrows="$vuetify.breakpoint.smAndDown"
            >
              <v-tab
                v-for="item in tabMenus"
                :key="item.value"
                class="modern-tab"
              >
                <v-icon left>{{ item.icon }}</v-icon>
                <span v-if="!$vuetify.breakpoint.xs">{{ item.text }}</span>
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab" class="tab-content">
              <!-- Information Tab -->
              <v-tab-item>
                <v-card flat class="pa-4 pa-sm-6">
                  <div class="section-header mb-4 mb-sm-6">
                    <h2 class="section-title">Personal Information</h2>
                    <p class="section-subtitle">Manage your account details and preferences</p>
                  </div>

                  <v-form ref="profileForm" v-model="profileValid">
                    <v-row>
                      <!-- Personal Details Section -->
                      <v-col cols="12">
                        <div class="form-section">
                          <h3 class="form-section-title">Basic Details</h3>
                          <v-row>
                            <v-col cols="12" md="6">
                              <v-text-field
                                v-model="profile.first_name"
                                label="First Name"
                                :readonly="isViewMode"
                                :outlined="!isViewMode"
                                :filled="isViewMode"
                                prepend-inner-icon="mdi-account"
                                class="modern-input"
                                :rules="[rules.required]"
                              />
                            </v-col>
                            <v-col cols="12" md="6">
                              <v-text-field
                                v-model="profile.last_name"
                                label="Last Name"
                                :readonly="isViewMode"
                                :outlined="!isViewMode"
                                :filled="isViewMode"
                                prepend-inner-icon="mdi-account"
                                class="modern-input"
                                :rules="[rules.required]"
                              />
                            </v-col>
                          </v-row>
                        </div>
                      </v-col>

                      <!-- Contact Information Section -->
                      <v-col cols="12">
                        <div class="form-section">
                          <h3 class="form-section-title">Contact Information</h3>
                          <v-row>
                            <v-col cols="12">
                              <v-text-field
                                v-model="profile.email"
                                label="Email Address"
                                type="email"
                                :readonly="isViewMode"
                                :outlined="!isViewMode"
                                :filled="isViewMode"
                                prepend-inner-icon="mdi-email"
                                class="modern-input"
                                :rules="[rules.required, rules.email]"
                              />
                            </v-col>
                            <v-col cols="12" md="8">
                              <AppPhoneNumberInput
                                v-model="profile.phone"
                                @update="onUpdatePhone"
                                :readonly="isViewMode"
                                class="modern-input"
                              />
                            </v-col>
                            <v-col cols="12" md="4">
                              <v-text-field
                                v-model="profile.phone_ext"
                                label="Extension"
                                :readonly="isViewMode"
                                :outlined="!isViewMode"
                                :filled="isViewMode"
                                prepend-inner-icon="mdi-phone-plus"
                                class="modern-input"
                              />
                            </v-col>
                          </v-row>
                        </div>
                      </v-col>

                      <!-- Address Section -->
                      <v-col cols="12">
                        <div class="form-section">
                          <h3 class="form-section-title">Address Details</h3>
                          <v-row>
                            <v-col cols="12">
                              <v-text-field
                                v-model="profile.address"
                                label="Address Line 1"
                                :readonly="isViewMode"
                                :outlined="!isViewMode"
                                :filled="isViewMode"
                                prepend-inner-icon="mdi-map-marker"
                                class="modern-input"
                                :rules="[rules.required]"
                              />
                            </v-col>
                            <v-col cols="12">
                              <v-text-field
                                v-model="profile.address2"
                                label="Address Line 2 (Optional)"
                                :readonly="isViewMode"
                                :outlined="!isViewMode"
                                :filled="isViewMode"
                                prepend-inner-icon="mdi-map-marker-outline"
                                class="modern-input"
                              />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                              <v-text-field
                                v-model="profile.city"
                                label="City"
                                :readonly="isViewMode"
                                :outlined="!isViewMode"
                                :filled="isViewMode"
                                prepend-inner-icon="mdi-city"
                                class="modern-input"
                                :rules="[rules.required]"
                              />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                              <v-select
                                v-model="profile.state"
                                :items="stateOptions"
                                label="State"
                                :readonly="isViewMode"
                                :outlined="!isViewMode"
                                :filled="isViewMode"
                                prepend-inner-icon="mdi-map"
                                class="modern-input"
                                :rules="[rules.required]"
                              />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                              <v-text-field
                                v-model="profile.zip"
                                label="ZIP Code"
                                :readonly="isViewMode"
                                :outlined="!isViewMode"
                                :filled="isViewMode"
                                prepend-inner-icon="mdi-postcode"
                                class="modern-input"
                                :rules="[rules.required, rules.zip]"
                                type="text"
                                autocomplete="off"
                              />
                            </v-col>
                          </v-row>
                        </div>
                      </v-col>

                      <!-- Action Buttons -->
                      <v-col cols="12" v-if="!isViewMode">
                        <div class="form-actions">
                          <v-btn
                            color="primary"
                            large
                            @click="updateProfile"
                            :loading="loading"
                            class="save-btn"
                            block
                          >
                            <v-icon left>mdi-content-save</v-icon>
                            Save Changes
                          </v-btn>
                          <v-btn
                            text
                            large
                            @click="resetForm"
                            class="ml-0 ml-sm-4 mt-2 mt-sm-0"
                            block
                          >
                            <v-icon left>mdi-refresh</v-icon>
                            Reset
                          </v-btn>
                        </div>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-card>
              </v-tab-item>

              <!-- Security Tab -->
              <v-tab-item>
                <v-card flat class="pa-4 pa-sm-6">
                  <div class="section-header mb-4 mb-sm-6">
                    <h2 class="section-title">Security Settings</h2>
                    <p class="section-subtitle">Update your password and security preferences</p>
                  </div>

                  <div class="security-section">
                    <v-row>
                      <!-- Password Strength Indicator -->
                      <v-col cols="12">
                        <v-alert
                          :type="passwordStrength.type"
                          :icon="passwordStrength.icon"
                          outlined
                          class="mb-4"
                        >
                          {{ passwordStrength.message }}
                        </v-alert>
                      </v-col>

                      <v-col cols="12">
                        <v-text-field
                          v-model="oldPassword"
                          label="Current Password"
                          type="password"
                          outlined
                          prepend-inner-icon="mdi-lock"
                          class="modern-input"
                          :rules="[rules.required, rules.min]"
                          autocomplete="current-password"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="newPassword"
                          label="New Password"
                          type="password"
                          outlined
                          prepend-inner-icon="mdi-lock-plus"
                          class="modern-input"
                          :rules="[rules.required, rules.min, rules.passwordStrength]"
                          autocomplete="new-password"
                          @input="checkPasswordStrength"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="confirmPassword"
                          label="Confirm New Password"
                          type="password"
                          outlined
                          prepend-inner-icon="mdi-lock-check"
                          class="modern-input"
                          :rules="[rules.required, rules.min, rules.passwordMatch]"
                          autocomplete="new-password"
                        />
                      </v-col>
                      <v-col cols="12">
                        <div class="form-actions">
                          <v-btn
                            color="success"
                            large
                            @click="updatePassword"
                            :loading="loading"
                            class="save-btn"
                            block
                          >
                            <v-icon left>mdi-shield-check</v-icon>
                            Update Password
                          </v-btn>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-card>
              </v-tab-item>

              <!-- Activity Tab -->
              <v-tab-item>
                <v-card flat class="pa-4 pa-sm-6">
                  <div class="section-header mb-4 mb-sm-6">
                    <h2 class="section-title">Account Activity</h2>
                    <p class="section-subtitle">Monitor your account activity and preferences</p>
                  </div>

                  <div class="activity-section">
                    <v-row>
                      <v-col cols="12" md="6" class="mb-4 mb-md-0">
                        <v-card outlined class="activity-card h-100">
                          <v-card-title>
                            <v-icon color="primary" class="mr-2">mdi-clock</v-icon>
                            Last Login
                          </v-card-title>
                          <v-card-text>
                            <p class="text-h6">{{ formatDate(new Date()) }}</p>
                            <p class="text-caption">From Salem, Tamil Nadu</p>
                          </v-card-text>
                        </v-card>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-card outlined class="activity-card h-100">
                          <v-card-title>
                            <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                            Account Status
                          </v-card-title>
                          <v-card-text>
                            <p class="text-h6 success--text">Active</p>
                            <p class="text-caption">All systems operational</p>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Snackbar for notifications -->
    <v-snackbar 
      v-model="snackbar" 
      :color="snackbarColor"
      timeout="4000"
      top
      right
    >
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Loading overlay -->
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      />
    </v-overlay>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";
import AppPhoneNumberInput from "@/components/common/AppPhoneNumberInput.vue";

export default {
  components: { EditableAvatar, AppPhoneNumberInput },
  data() {
    return {
      isViewMode: true,
      profileValid: false,
      snackbarColor: 'error',
      rules: {
        required: value => !!value || "This field is required",
        email: value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Please enter a valid email address";
        },
        phone: value => {
          const pattern = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
          return pattern.test(value) || "Please enter a valid phone number";
        },
        zip: value => {
          const pattern = /^\d{5}(-\d{4})?$/;
          return pattern.test(value) || "Please enter a valid ZIP code";
        },
        min: v => (v && v.length >= 8) || "Password must be at least 8 characters",
        passwordStrength: v => {
          if (!v) return true;
          const hasUpper = /[A-Z]/.test(v);
          const hasLower = /[a-z]/.test(v);
          const hasNumber = /\d/.test(v);
          const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(v);
          return (hasUpper && hasLower && hasNumber && hasSpecial) || 
                 "Password must contain uppercase, lowercase, number, and special character";
        },
        passwordMatch: () =>
          this.newPassword === this.confirmPassword || "Passwords do not match"
      },
      stateOptions: [
        "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
        "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
        "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
        "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
        "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
        "AS", "DC", "FM", "GU", "MH", "MP", "PW", "PR", "VI"
      ],
      phonePayload: {},
      loading: false,
      selectedImage: null,
      errorMessage: null,
      snackbar: false,
      tab: null,
      tabMenus: [
        { text: "Information", value: "info", icon: "mdi-account-edit" },
        { text: "Security", value: "security", icon: "mdi-shield-lock" },
        { text: "Activity", value: "activity", icon: "mdi-chart-line" }
      ],
      newPassword: "",
      confirmPassword: "",
      oldPassword: "",
      passwordStrength: {
        type: 'info',
        icon: 'mdi-information',
        message: 'Password should be at least 8 characters with mixed case, numbers, and symbols'
      }
    };
  },
  methods: {
    ...mapActions("auth", {
      fetchProfile: "fetchProfile",
      updateAdmin: "updateAdmin",
      updateAdminPassword: "updateAdminPassword"
    }),
    toggleViewMode() {
      this.isViewMode = !this.isViewMode;
    },
    exportProfile() {
      this.showSuccessMessage("Profile exported successfully");
    },
    resetForm() {
      this.$refs.profileForm.reset();
      this.fetchProfile();
    },
    onUpdatePhone(payload) {
      this.phonePayload = payload;
    },
    updateProfile() {
      if (!this.$refs.profileForm.validate()) {
        this.showErrorMessage("Please fix the form errors before saving");
        return;
      }

      const formData = new FormData();
      this.loading = true;
      
      Object.keys(this.profile).forEach(key => {
        if (this.profile[key] !== undefined) {
          formData.append(key, this.profile[key]);
        }
      });
      
      if (this.selectedImage) {
        formData.append("image", this.selectedImage);
      }
      
      this.updateAdmin(formData)
        .then(() => {
          this.loading = false;
          this.showSuccessMessage("Profile updated successfully");
          this.fetchProfile();
        })
        .catch(error => {
          this.loading = false;
          this.showErrorMessage(error.response?.data?.message || "Failed to update profile");
        });
    },
    updatePassword() {
      if (!this.oldPassword || !this.newPassword || !this.confirmPassword) {
        this.showErrorMessage("Please fill in all password fields");
        return;
      }

      if (this.newPassword !== this.confirmPassword) {
        this.showErrorMessage("New passwords do not match");
        return;
      }

      this.loading = true;
      this.updateAdminPassword({
        newPassword: this.newPassword,
        oldPassword: this.oldPassword
      })
        .then(() => {
          this.loading = false;
          this.showSuccessMessage("Password updated successfully");
          this.oldPassword = "";
          this.newPassword = "";
          this.confirmPassword = "";
        })
        .catch(error => {
          this.loading = false;
          this.showErrorMessage(error.response?.data?.message || "Failed to update password");
        });
    },
    checkPasswordStrength() {
      if (!this.newPassword) {
        this.passwordStrength = {
          type: 'info',
          icon: 'mdi-information',
          message: 'Password should be at least 8 characters with mixed case, numbers, and symbols'
        };
        return;
      }

      const hasUpper = /[A-Z]/.test(this.newPassword);
      const hasLower = /[a-z]/.test(this.newPassword);
      const hasNumber = /\d/.test(this.newPassword);
      const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(this.newPassword);
      const isLongEnough = this.newPassword.length >= 8;

      if (hasUpper && hasLower && hasNumber && hasSpecial && isLongEnough) {
        this.passwordStrength = {
          type: 'success',
          icon: 'mdi-check-circle',
          message: 'Strong password! All requirements met.'
        };
      } else if (isLongEnough && (hasUpper || hasLower) && hasNumber) {
        this.passwordStrength = {
          type: 'warning',
          icon: 'mdi-alert',
          message: 'Good password, but consider adding special characters for better security.'
        };
      } else {
        this.passwordStrength = {
          type: 'error',
          icon: 'mdi-alert-circle',
          message: 'Weak password. Please include uppercase, lowercase, numbers, and special characters.'
        };
      }
    },
    onImageSelected(image) {
      this.selectedImage = image;
    },
    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
    showSuccessMessage(message) {
      this.errorMessage = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    })
  },
  created() {
    this.loading = true;
    this.fetchProfile()
      .then(() => {
        this.loading = false;
      })
      .catch(error => {
        this.loading = false;
        if (error.response?.status === 401) {
          this.tokenDialog = true;
        } else {
          this.showErrorMessage("Failed to load profile");
        }
      });
  }
};
</script>

<style scoped>
/* ==================== Base Styles ==================== */
.profile-container {
  min-height: 100vh;
  position: relative;
  width: 100%;
  overflow-x: hidden;
}

/* ==================== Hero Section ==================== */
.hero-section {
  height: 160px;
  position: relative;
  overflow: hidden;
}

.hero-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  opacity: 0.9;
}

.hero-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 20% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%);
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
}

/* ==================== Main Content ==================== */
.main-content {
  margin-top: -100px;
  position: relative;
  z-index: 2;
  padding: 0 16px 24px;
}

/* ==================== Profile Header Card ==================== */
.profile-header-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 16px !important;
  border: 1px solid rgba(255, 255, 255, 0.5);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.profile-header-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12) !important;
}

.profile-header-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.avatar-container {
  position: relative;
}

.profile-avatar {
  border: 4px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.profile-avatar:hover {
  transform: scale(1.05);
}

.avatar-status-indicator {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 16px;
  height: 16px;
  background: #4CAF50;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

/* ==================== Profile Info ==================== */
.profile-info {
  width: 100%;
  text-align: center;
}

.profile-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.3;
  word-wrap: break-word;
}

.profile-email {
  color: #64748b;
  font-size: 0.9375rem;
  margin: 4px 0;
  word-break: break-word;
  line-height: 1.5;
}

.profile-phone {
  color: #64748b;
  font-size: 0.9375rem;
  margin: 4px 0;
  line-height: 1.5;
}

.profile-badges {
  margin-top: 12px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* ==================== Action Buttons ==================== */
.action-buttons {
  display: flex;
  width: 100%;
  max-width: 100%;
}

.action-btn {
  border-radius: 12px !important;
  text-transform: none;
  font-weight: 600;
  padding: 0 24px !important;
  height: 44px !important;
  width: 100%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

/* ==================== Main Content Card ==================== */
.main-content-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 16px !important;
  border: 1px solid rgba(255, 255, 255, 0.5);
  overflow: hidden;
}

/* ==================== Tabs ==================== */
.modern-tabs {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 16px 16px 0 0;
  padding: 0 8px;
}

.modern-tab {
  text-transform: none;
  font-weight: 600;
  color: #475569;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 80px;
  padding: 0 16px;
  height: 56px;
  border-radius: 8px;
  margin: 4px;
}

.modern-tab:hover {
  background: rgba(255, 255, 255, 0.6);
  color: #1e293b;
}

.tab-content {
  background: rgba(255, 255, 255, 0.98);
}

/* ==================== Section Headers ==================== */
.section-header {
  text-align: center;
  margin-bottom: 32px;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
  line-height: 1.3;
}

.section-subtitle {
  color: #64748b;
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
}

/* ==================== Form Sections ==================== */
.form-section {
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.6) 0%, rgba(241, 245, 249, 0.4) 100%);
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.form-section:hover {
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.7) 100%);
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
}

.form-section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-section-title::before {
  content: '';
  width: 4px;
  height: 20px;
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  border-radius: 3px;
  flex-shrink: 0;
}

.modern-input {
  transition: all 0.3s ease;
}

.modern-input:deep(.v-input__control) {
  border-radius: 12px !important;
}

.modern-input:deep(.v-text-field__details) {
  padding-top: 8px;
}

.modern-input:deep(.v-input--is-focused .v-input__control) {
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

/* ==================== Form Actions ==================== */
.form-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid rgba(226, 232, 240, 0.8);
  width: 100%;
}

.save-btn {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  text-transform: none;
  font-weight: 600;
  padding: 0 24px !important;
  height: 48px !important;
  box-shadow: 0 4px 16px rgba(8, 68, 104, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  font-size: 0.9375rem;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(8, 68, 104, 0.4) !important;
}

.save-btn:disabled {
  opacity: 0.6;
  transform: none;
  box-shadow: none !important;
}

.security-section {
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.activity-section {
  background: rgba(248, 250, 252, 0.5);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.activity-card {
  border-radius: 12px !important;
  border: 1px solid rgba(226, 232, 240, 0.8) !important;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8) !important;
  height: 100%;
}

.activity-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important;
}

.activity-card:deep(.v-card__title) {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px 12px 0 0;
  font-weight: 600;
  color: #374151;
  padding: 1rem;
}

.activity-card:deep(.v-card__text) {
  padding: 1rem;
}

.h-100 {
  height: 100%;
}

/* ==================== RESPONSIVE BREAKPOINTS ==================== */

/* Small Mobile (320px - 479px) */
@media (max-width: 479px) {
  .main-content {
    padding: 0 12px 20px;
  }
  
  .profile-header-content {
    padding: 20px;
  }
  
  .profile-name {
    font-size: 1.375rem;
  }
  
  .section-title {
    font-size: 1.5rem;
  }
  
  .form-section {
    padding: 16px;
  }
  
  .modern-tab {
    min-width: 70px;
    padding: 0 12px;
    font-size: 0.8125rem;
  }
}

/* Mobile (480px - 767px) */
@media (min-width: 480px) and (max-width: 767px) {
  .main-content {
    padding: 0 16px 24px;
  }
  
  .profile-name {
    font-size: 1.5rem;
  }
  
  .modern-tab {
    min-width: 90px;
  }
}

/* Tablet Portrait (768px - 1023px) */
@media (min-width: 768px) {
  .hero-section {
    height: 200px;
  }
  
  .main-content {
    margin-top: -130px;
    padding: 0 24px 32px;
  }
  
  .profile-header-content {
    padding: 32px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }
  
  .avatar-section {
    flex-direction: row;
    align-items: center;
    gap: 20px;
    width: auto;
  }
  
  .profile-info {
    text-align: left;
  }
  
  .profile-name {
    font-size: 1.75rem;
  }
  
  .profile-email,
  .profile-phone {
    font-size: 1rem;
  }
  
  .profile-badges {
    justify-content: flex-start;
  }
  
  .action-buttons {
    width: auto;
    min-width: 200px;
  }
  
  .action-btn {
    width: auto;
    min-width: 200px;
  }
  
  .modern-tab {
    min-width: 120px;
    padding: 0 20px;
  }
  
  .form-section {
    padding: 24px;
  }
  
  .form-actions {
    flex-direction: row;
    justify-content: flex-start;
  }
  
  .save-btn {
    width: auto;
    min-width: 200px;
  }
}

/* Tablet Landscape & Small Desktop (1024px - 1279px) */
@media (min-width: 1024px) {
  .hero-section {
    height: 240px;
  }
  
  .main-content {
    margin-top: -160px;
    padding: 0 32px 40px;
  }
  
  .profile-header-content {
    padding: 40px;
  }
  
  .profile-name {
    font-size: 2rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .section-subtitle {
    font-size: 1.125rem;
  }
  
  .form-section {
    padding: 28px;
  }
  
  .form-section-title {
    font-size: 1.25rem;
  }
  
  .modern-tabs {
    padding: 0 12px;
  }
  
  .modern-tab {
    padding: 0 24px;
  }
}

/* Animation Classes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from {
  transform: translateX(100%);
}

.slide-leave-to {
  transform: translateX(-100%);
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(241, 245, 249, 0.5);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
}

/* Input Focus States */
.modern-input:deep(.v-input--is-focused .v-label) {
  color: #667eea !important;
}

.modern-input:deep(.v-input--is-focused .v-input__control .v-input__slot) {
  border-color: #667eea !important;
}

/* Chip Styling */
.v-chip {
  border-radius: 20px !important;
  font-weight: 500;
}

/* Tab Styling */
.v-tabs:deep(.v-tab--active) {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
}

.v-tabs:deep(.v-tabs-slider) {
  height: 3px !important;
  border-radius: 2px;
}

/* Card Elevation Override */
.elevation-8 {
  box-shadow: 0 8px 32px rgba(0,0,0,0.1) !important;
}

.elevation-12 {
  box-shadow: 0 12px 40px rgba(0,0,0,0.15) !important;
}

/* Snackbar Custom Styling */
.v-snackbar {
  border-radius: 12px !important;
}

.v-snackbar:deep(.v-snack__wrapper) {
  border-radius: 12px !important;
  backdrop-filter: blur(10px);
}

/* Alert Styling */
.v-alert {
  border-radius: 12px !important;
  border-left: 4px solid !important;
}

.v-alert:deep(.v-alert__content) {
  font-weight: 500;
}

/* Select Dropdown */
.v-select:deep(.v-select__selection) {
  color: #374151 !important;
}

.v-select:deep(.v-input__icon) {
  color: #6b7280 !important;
}

/* Form Validation */
.v-text-field:deep(.v-messages__message) {
  color: #ef4444 !important;
  font-size: 0.875rem;
}

.v-text-field--error:deep(.v-input__control) {
  border-color: #ef4444 !important;
}

/* Button Hover Effects */
.v-btn:not(.save-btn) {
  transition: all 0.3s ease;
}

.v-btn:not(.save-btn):hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}


/* Desktop (1280px - 1919px) */
@media (min-width: 1280px) {
  .hero-section {
    height: 280px;
  }
  
  .main-content {
    margin-top: -200px;
  }
  
  .profile-header-content {
    padding: 48px;
  }
  
  .profile-name {
    font-size: 2.125rem;
  }
  
  .section-title {
    font-size: 2.25rem;
  }
  
  .form-section {
    padding: 32px;
    margin-bottom: 28px;
  }
}

/* Large Desktop (1920px and up) */
@media (min-width: 1920px) {
  .hero-section {
    height: 320px;
  }
  
  .main-content {
    margin-top: -240px;
  }
  
  .profile-header-content {
    padding: 56px;
  }
  
  .profile-name {
    font-size: 2.25rem;
  }
  
  .section-title {
    font-size: 2.5rem;
  }
  
  .form-section {
    padding: 40px;
  }
}

/* ==================== Additional Responsive Utilities ==================== */

/* Tab Scrolling on Small Screens */
@media (max-width: 767px) {
  .modern-tabs {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  
  .modern-tabs::-webkit-scrollbar {
    display: none;
  }
}

/* Form Field Spacing */
.v-text-field {
  margin-bottom: 12px;
}

@media (min-width: 768px) {
  .v-text-field {
    margin-bottom: 16px;
  }
}

@media (min-width: 1024px) {
  .v-text-field {
    margin-bottom: 20px;
  }
}

/* Chip Sizes */
.v-chip {
  font-size: 0.75rem;
  height: 26px;
}

@media (min-width: 768px) {
  .v-chip {
    font-size: 0.8125rem;
    height: 28px;
  }
}

@media (min-width: 1024px) {
  .v-chip {
    font-size: 0.875rem;
    height: 32px;
  }
}

/* Activity Cards */
.activity-card {
  margin-bottom: 16px;
}

@media (min-width: 768px) {
  .activity-card {
    margin-bottom: 0;
    height: 100%;
  }
}
</style>